// src/utils/fishingUtils.js
import { ItemData } from '../data/ItemData';

// Fishing Zones Configuration
export const FISHING_ZONES = {
  SHALLOW_POND: {
    id: 'shallow_pond',
    name: 'Shallow Pond',
    description: 'A calm pond perfect for beginners',
    requiredFishingTier: 0,
    fishTiers: [0, 1],
    specialFish: ['minnow', 'anchovy', 'sardine', 'chub', 'sunfish', 'perch'],
    bonuses: { easierMinigame: 0.1 },
    img: 'backgrounds/shallow_pond.png'
  },
  FOREST_STREAM: {
    id: 'forest_stream',
    name: 'Forest Stream',
    description: 'A babbling brook with moderate catches',
    requiredFishingTier: 1,
    fishTiers: [1, 2],
    specialFish: ['bream', 'bullhead', 'catfish', 'carp', 'crayfish', 'shrimp'],
    bonuses: { attractFish: 0.05 },
    img: 'backgrounds/forest_stream.png'
  },
  MOUNTAIN_LAKE: {
    id: 'mountain_lake',
    name: 'Mountain Lake',
    description: 'A pristine lake with trout and bass',
    requiredFishingTier: 2,
    fishTiers: [2, 3, 4],
    specialFish: ['smallmouth_bass', 'largemouth_bass', 'rainbow_trout', 'tiger_trout', 'pike', 'walleye'],
    bonuses: { rareFish: 0.1 },
    img: 'backgrounds/mountain_lake.png'
  },
  COASTAL_WATERS: {
    id: 'coastal_waters',
    name: 'Coastal Waters',
    description: 'Saltwater fishing with marine life',
    requiredFishingTier: 3,
    fishTiers: [3, 4, 5],
    specialFish: ['salmon', 'steelhead', 'red_snapper', 'red_mullet', 'crab', 'lobster', 'oyster', 'mussel', 'clam'],
    bonuses: { fishValue: 0.15 },
    img: 'backgrounds/coastal_waters.png'
  },
  DEEP_OCEAN: {
    id: 'deep_ocean',
    name: 'Deep Ocean',
    description: 'Open ocean with large game fish',
    requiredFishingTier: 4,
    fishTiers: [4, 5, 6, 7],
    specialFish: ['tuna', 'albacore', 'dorado', 'halibut', 'sturgeon', 'lingcod', 'lionfish', 'pufferfish', 'stingray'],
    bonuses: { legendaryFish: 0.05, predatoryFish: 0.2 },
    img: 'backgrounds/deep_ocean.png'
  },
  ABYSSAL_DEPTHS: {
    id: 'abyssal_depths',
    name: 'Abyssal Depths',
    description: 'Mysterious depths with rare creatures',
    requiredFishingTier: 6,
    fishTiers: [6, 7, 8, 9],
    specialFish: ['angler', 'eel', 'octopus', 'squid', 'midnight_squid', 'midnight_carp', 'scorpion_carp'],
    bonuses: { unusualFish: 0.3, nightFishing: true },
    img: 'backgrounds/abyssal_depths.png'
  },
  VOID_WATERS: {
    id: 'void_waters',
    name: 'Void Waters',
    description: 'Otherworldly waters with mythical fish',
    requiredFishingTier: 8,
    fishTiers: [8, 9, 10, 11],
    specialFish: ['mutant_carp', 'stonefish', 'lava_eel', 'void_salmon', 'glacierfish', 'ice_pip', 'crimsonfish'],
    bonuses: { voidFish: 0.5, ancientFish: 0.2, legendaryFish: 0.1 },
    img: 'backgrounds/void_waters.png'
  },
  LEGENDARY_REALM: {
    id: 'legendary_realm',
    name: 'Legendary Realm',
    description: 'A realm where only legends swim',
    requiredFishingTier: 10,
    fishTiers: [10, 11, 12],
    specialFish: ['legend', 'spook_fish', 'slimejack', 'woodskip', 'super_cucumber', 'sea_cucumber', 'sandfish'],
    bonuses: { legendaryFish: 0.2, allFish: 0.1, treasureChance: 0.1 },
    img: 'backgrounds/legendary_realm.png'
  }
};

// Get fishing tier from equipped fishing pole
export const getFishingTier = (playerData) => {
  const mainHandItemId = playerData.equipment.mainHand;
  const tool = mainHandItemId ? playerData.inventory[mainHandItemId] : null;
  
  if (!tool || typeof tool !== 'object' || tool.toolType !== 'fishingRod') {
    return 0; // No fishing rod equipped
  }
  
  return tool.fishingTier || 0;
};

// Get available fishing zones based on player's fishing tier
export const getAvailableFishingZones = (playerFishingTier) => {
  return Object.values(FISHING_ZONES).filter(zone => 
    zone.requiredFishingTier <= playerFishingTier
  );
};

// Calculate fishing bonuses from all equipped gear
export const calculateFishingBonuses = (playerData) => {
  const bonuses = {
    successRate: 0,
    doubleCatch: 0,
    rareFish: 0,
    legendaryFish: 0,
    easierMinigame: 0,
    escapeReduction: 0,
    energyCost: 0,
    xpBonus: 0,
    waitTime: 0,
    attractFish: 0,
    fishVariety: 0,
    fishValue: 0,
    rareFishValue: 0,
    carryCapacity: 0,
    betterSpots: false,
    nightFishing: false,
    dangerousWaters: false,
    // Special fish type bonuses
    predatoryFish: 0,
    unusualFish: 0,
    allFish: 0,
    ancientFish: 0,
    voidFish: 0,
    treasureChance: 0,
    fishQuality: 0,
    fishQuantity: 0,
    biteDetection: 0,
    castingRange: 0,
    deepWater: false,
    sinkRate: 0,
    bonusAttempts: 0
  };
  
  // Check all equipment slots for fishing bonuses
  const equipmentSlots = ['head', 'chest', 'legs', 'feet', 'hands', 'ring1', 'ring2', 'amulet', 'belt'];
  
  equipmentSlots.forEach(slot => {
    const itemId = playerData.equipment[slot];
    if (itemId && playerData.inventory[itemId]) {
      const item = playerData.inventory[itemId];
      const itemData = ItemData[item.key];
      
      if (itemData && itemData.fishingBonuses) {
        Object.keys(itemData.fishingBonuses).forEach(bonusType => {
          if (bonuses.hasOwnProperty(bonusType)) {
            if (typeof bonuses[bonusType] === 'boolean') {
              bonuses[bonusType] = bonuses[bonusType] || itemData.fishingBonuses[bonusType];
            } else {
              bonuses[bonusType] += itemData.fishingBonuses[bonusType];
            }
          }
        });
      }
    }
  });
  
  return bonuses;
};

// Get available fish for current zone and tier
export const getAvailableFish = (fishingTier, bonuses = {}, selectedZone = null) => {
  const allFish = [];
  
  // If no zone selected, use wilderness (all fish up to tier)
  if (!selectedZone) {
    // Get fish for current tier and all lower tiers
    Object.keys(ItemData).forEach(fishKey => {
      const fish = ItemData[fishKey];
      if (fish.category === 'fish' && fish.fishingTier <= fishingTier) {
        allFish.push({
          key: fishKey,
          name: fish.name,
          difficulty: fish.difficulty,
          tier: fish.fishingTier,
          weight: fish.weight,
          goldValue: fish.goldValue,
          img: fish.img
        });
      }
    });
    
    // Add junk items (fishingTier: -1)
    Object.keys(ItemData).forEach(fishKey => {
      const fish = ItemData[fishKey];
      if (fish.category === 'junk' && fish.fishingTier === -1) {
        allFish.push({
          key: fishKey,
          name: fish.name,
          difficulty: fish.difficulty,
          tier: fish.fishingTier,
          weight: fish.weight,
          goldValue: fish.goldValue,
          img: fish.img
        });
      }
    });
    
    return allFish;
  }
  
  // Get fish available in the selected zone
  const zone = FISHING_ZONES[selectedZone];
  if (!zone) return allFish;
  
  // Add zone-specific fish
  zone.specialFish.forEach(fishKey => {
    const fish = ItemData[fishKey];
    if (fish) {
      allFish.push({
        key: fishKey,
        name: fish.name,
        difficulty: fish.difficulty,
        tier: fish.fishingTier,
        weight: fish.weight,
        goldValue: fish.goldValue,
        img: fish.img
      });
    }
  });
  
  // Add fish from allowed tiers in this zone
  Object.keys(ItemData).forEach(fishKey => {
    const fish = ItemData[fishKey];
    if (fish.category === 'fish' && 
        zone.fishTiers.includes(fish.fishingTier) &&
        !zone.specialFish.includes(fishKey)) {
      allFish.push({
        key: fishKey,
        name: fish.name,
        difficulty: fish.difficulty,
        tier: fish.fishingTier,
        weight: fish.weight,
        goldValue: fish.goldValue,
        img: fish.img
      });
    }
  });
  
  // Add junk items (can be caught anywhere)
  Object.keys(ItemData).forEach(fishKey => {
    const fish = ItemData[fishKey];
    if (fish.category === 'junk' && fish.fishingTier === -1) {
      allFish.push({
        key: fishKey,
        name: fish.name,
        difficulty: fish.difficulty,
        tier: fish.fishingTier,
        weight: fish.weight,
        goldValue: fish.goldValue,
        img: fish.img
      });
    }
  });
  
  return allFish;
};

// Select a fish based on tier, bonuses, and weighted probability
export const selectFish = (fishingTier, bonuses = {}, playerFishingSkill = 0, selectedZone = null) => {
  const availableFish = getAvailableFish(fishingTier, bonuses, selectedZone);
  
  if (availableFish.length === 0) {
    return null;
  }
  
  // Get zone bonuses
  const zone = selectedZone ? FISHING_ZONES[selectedZone] : null;
  const zoneBonuses = zone ? zone.bonuses : {};
  
  // Combine bonuses
  const combinedBonuses = { ...bonuses };
  Object.keys(zoneBonuses).forEach(bonusType => {
    if (typeof combinedBonuses[bonusType] === 'boolean') {
      combinedBonuses[bonusType] = combinedBonuses[bonusType] || zoneBonuses[bonusType];
    } else {
      combinedBonuses[bonusType] = (combinedBonuses[bonusType] || 0) + zoneBonuses[bonusType];
    }
  });
  
  // Calculate weights for each fish
  const weightedFish = availableFish.map(fish => {
    let baseWeight = 1;
    
    // Tier-based weighting (higher tier = rarer, but adjusted for rod quality)
    if (fish.tier >= 0) {
      const tierDifference = fishingTier - fish.tier;
      baseWeight = Math.max(0.1, 1 - (fish.tier * 0.06) + (tierDifference * 0.05));
    }
    
    // Difficulty-based weighting
    switch (fish.difficulty) {
      case 'junk':
        baseWeight = combinedBonuses.treasureChance > 0 ? 0.2 : 0.6; // Less junk with better gear
        break;
      case 'easy':
        baseWeight *= 1.3;
        break;
      case 'medium':
        baseWeight *= 1.0;
        break;
      case 'hard':
        baseWeight *= 0.8;
        break;
      case 'legendary':
        baseWeight *= 0.15;
        break;
    }
    
    // Skill-based modifier (higher skill = better rare fish chances)
    const skillModifier = 1 + (playerFishingSkill * 0.03);
    baseWeight *= skillModifier;
    
    // Rod tier advantage (better rods increase chances for higher tier fish)
    if (fish.tier > 0 && fishingTier >= fish.tier) {
      const rodAdvantage = 1 + ((fishingTier - fish.tier) * 0.1);
      baseWeight *= rodAdvantage;
    }
    
    // Apply bonuses
    if (fish.difficulty === 'legendary' && combinedBonuses.legendaryFish > 0) {
      baseWeight *= (1 + combinedBonuses.legendaryFish * 5);
    }
    
    if (fish.difficulty === 'hard' && combinedBonuses.rareFish > 0) {
      baseWeight *= (1 + combinedBonuses.rareFish * 3);
    }
    
    if (combinedBonuses.allFish > 0) {
      baseWeight *= (1 + combinedBonuses.allFish);
    }
    
    // Special fish type bonuses
    if (fish.tier >= 5 && combinedBonuses.predatoryFish > 0) {
      baseWeight *= (1 + combinedBonuses.predatoryFish);
    }
    
    if (fish.tier >= 7 && combinedBonuses.unusualFish > 0) {
      baseWeight *= (1 + combinedBonuses.unusualFish);
    }
    
    if (fish.tier >= 9 && combinedBonuses.ancientFish > 0) {
      baseWeight *= (1 + combinedBonuses.ancientFish);
    }
    
    if (fish.tier >= 10 && combinedBonuses.voidFish > 0) {
      baseWeight *= (1 + combinedBonuses.voidFish);
    }
    
    return {
      ...fish,
      weight: Math.max(0.01, baseWeight)
    };
  });
  
  // Calculate total weight
  const totalWeight = weightedFish.reduce((sum, fish) => sum + fish.weight, 0);
  
  // Select random fish based on weight
  let random = Math.random() * totalWeight;
  
  for (const fish of weightedFish) {
    random -= fish.weight;
    if (random <= 0) {
      return fish;
    }
  }
  
  // Fallback to first fish
  return weightedFish[0];
};

// Get fish difficulty multipliers for the minigame (improved with rod tier)
export const getFishDifficultyMultipliers = (fish, bonuses = {}, fishingTier = 0) => {
  const baseMultipliers = {
    speed: 1,
    directionChangeInterval: 1,
    escapeChance: 1,
    catchDifficulty: 1
  };
  
  // Difficulty-based multipliers
  switch (fish.difficulty) {
    case 'junk':
      baseMultipliers.speed = 0.5;
      baseMultipliers.directionChangeInterval = 2.0;
      baseMultipliers.escapeChance = 0.3;
      baseMultipliers.catchDifficulty = 0.5;
      break;
    case 'easy':
      baseMultipliers.speed = 0.8;
      baseMultipliers.directionChangeInterval = 1.5;
      baseMultipliers.escapeChance = 0.6;
      baseMultipliers.catchDifficulty = 0.8;
      break;
    case 'medium':
      baseMultipliers.speed = 1.0;
      baseMultipliers.directionChangeInterval = 1.0;
      baseMultipliers.escapeChance = 1.0;
      baseMultipliers.catchDifficulty = 1.0;
      break;
    case 'hard':
      baseMultipliers.speed = 1.3;
      baseMultipliers.directionChangeInterval = 0.8;
      baseMultipliers.escapeChance = 1.4;
      baseMultipliers.catchDifficulty = 1.3;
      break;
    case 'legendary':
      baseMultipliers.speed = 1.8;
      baseMultipliers.directionChangeInterval = 0.5;
      baseMultipliers.escapeChance = 2.0;
      baseMultipliers.catchDifficulty = 1.8;
      break;
  }
  
  // Rod tier advantage - better rods make fishing easier
  if (fishingTier > 0 && fish.tier >= 0) {
    const rodAdvantage = Math.min(0.4, fishingTier * 0.05); // Max 40% easier
    baseMultipliers.catchDifficulty *= (1 - rodAdvantage);
    baseMultipliers.escapeChance *= (1 - rodAdvantage * 0.5);
    baseMultipliers.speed *= (1 - rodAdvantage * 0.3);
  }
  
  // Apply bonuses
  if (bonuses.easierMinigame > 0) {
    baseMultipliers.catchDifficulty *= (1 - bonuses.easierMinigame);
    baseMultipliers.speed *= (1 - bonuses.easierMinigame * 0.5);
  }
  
  if (bonuses.escapeReduction > 0) {
    baseMultipliers.escapeChance *= (1 - bonuses.escapeReduction);
  }
  
  return baseMultipliers;
};

// Calculate fishing energy cost with bonuses
export const calculateFishingEnergyCost = (baseCost, bonuses = {}) => {
  let finalCost = baseCost;
  
  if (bonuses.energyCost !== 0) {
    finalCost = Math.max(0, finalCost * (1 + bonuses.energyCost));
  }
  
  return Math.floor(finalCost);
};

// Calculate fishing XP gain with bonuses
export const calculateFishingXPGain = (baseXP, bonuses = {}) => {
  let finalXP = baseXP;
  
  if (bonuses.xpBonus > 0) {
    finalXP *= (1 + bonuses.xpBonus);
  }
  
  return Math.floor(finalXP);
};

// Calculate fishing wait time with bonuses
export const calculateFishingWaitTime = (baseWaitTime, bonuses = {}) => {
  let finalWaitTime = baseWaitTime;
  
  if (bonuses.waitTime !== 0) {
    finalWaitTime = Math.max(1000, finalWaitTime * (1 + bonuses.waitTime));
  }
  
  if (bonuses.attractFish > 0) {
    finalWaitTime *= (1 - bonuses.attractFish * 0.3);
  }
  
  return Math.max(1000, finalWaitTime);
};

// Check if double catch should occur
export const shouldDoubleCatch = (bonuses = {}) => {
  return bonuses.doubleCatch > 0 && Math.random() < bonuses.doubleCatch;
};

// Calculate fish value with bonuses
export const calculateFishValue = (fish, bonuses = {}) => {
  let value = fish.goldValue;
  
  if (bonuses.fishValue > 0) {
    value *= (1 + bonuses.fishValue);
  }
  
  if (fish.difficulty === 'hard' && bonuses.rareFishValue > 0) {
    value *= (1 + bonuses.rareFishValue);
  }
  
  if (fish.difficulty === 'legendary' && bonuses.rareFishValue > 0) {
    value *= (1 + bonuses.rareFishValue * 2);
  }
  
  return Math.floor(value);
};

// Get fishing attachment bonuses
export const getFishingAttachmentBonuses = (playerData) => {
  const bonuses = {
    successRate: 0,
    escapeReduction: 0,
    rareFish: 0,
    legendaryFish: 0,
    attractFish: 0,
    fishQuality: 0,
    treasureChance: 0,
    deepWater: false,
    nightFishing: false,
    predatoryFish: 0,
    unusualFish: 0,
    allFish: 0,
    ancientFish: 0,
    voidFish: 0,
    fishQuantity: 0,
    biteDetection: 0,
    sinkRate: 0
  };
  
  // Check inventory for fishing attachments
  Object.keys(playerData.inventory).forEach(itemId => {
    const item = playerData.inventory[itemId];
    if (item && item.type === 'fishingAttachment') {
      const itemData = ItemData[item.key];
      if (itemData && itemData.fishingBonuses) {
        Object.keys(itemData.fishingBonuses).forEach(bonusType => {
          if (bonuses.hasOwnProperty(bonusType)) {
            if (typeof bonuses[bonusType] === 'boolean') {
              bonuses[bonusType] = bonuses[bonusType] || itemData.fishingBonuses[bonusType];
            } else {
              bonuses[bonusType] += itemData.fishingBonuses[bonusType];
            }
          }
        });
      }
    }
  });
  
  return bonuses;
};

// Combine all fishing bonuses (gear + attachments)
export const getAllFishingBonuses = (playerData) => {
  const gearBonuses = calculateFishingBonuses(playerData);
  const attachmentBonuses = getFishingAttachmentBonuses(playerData);
  
  const combinedBonuses = { ...gearBonuses };
  
  // Combine numeric bonuses
  Object.keys(attachmentBonuses).forEach(bonusType => {
    if (typeof combinedBonuses[bonusType] === 'number') {
      combinedBonuses[bonusType] += attachmentBonuses[bonusType];
    } else if (typeof combinedBonuses[bonusType] === 'boolean') {
      combinedBonuses[bonusType] = combinedBonuses[bonusType] || attachmentBonuses[bonusType];
    }
  });
  
  return combinedBonuses;
}; 